import CryptoJS from 'crypto-js'

// 백업 암호화·기록을 조각으로 나눠 메인 스레드를 오래 막지 않는다.
// A21s 과업 557(JSON 1.45MB) 기준 원샷 암호화 1.6초 + base64 우회 1.7초 + writeFile 0.6초가 한 번에 멈췄다.
// 백업 키는 표준 AES 길이가 아니라(crypto-js는 키 길이대로 라운드 수를 정함) WebCrypto로는 같은 암호문을 만들 수 없어
// crypto-js 점진 암호화기(process/finalize)를 그대로 쓴다 — 결과는 util.ts encryptData와 바이트 단위로 같다.

/** 암호화 조각 크기(평문 바이트). A21s에서 32KB가 약 60ms라 절반으로 둔다 */
const CRYPT_CHUNK_BYTES = 16 * 1024
/** base64 조각 크기(3의 배수여야 조각을 이어 붙인 결과가 한 번에 만든 것과 같다). A21s에서 192KB가 약 58ms */
const BASE64_CHUNK_BYTES = 3 * 32 * 1024

export const yieldToMain = (): Promise<void> => new Promise(resolve => setTimeout(resolve, 0))

function bytesToWordArray(bytes: Uint8Array): CryptoJS.lib.WordArray {
  const words: number[] = []
  for (let i = 0; i < bytes.length; i++) {
    words[i >>> 2] |= bytes[i] << (24 - (i % 4) * 8)
  }
  return CryptoJS.lib.WordArray.create(words, bytes.length)
}

function wordArrayToBytes(wordArray: CryptoJS.lib.WordArray): Uint8Array {
  const { words, sigBytes } = wordArray
  const out = new Uint8Array(sigBytes)
  for (let i = 0; i < sigBytes; i++) {
    out[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff
  }
  return out
}

async function bytesToBase64(bytes: Uint8Array): Promise<string> {
  const parts: string[] = []
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_BYTES) {
    const chunk = bytes.subarray(offset, offset + BASE64_CHUNK_BYTES)
    let binary = ''
    for (let i = 0; i < chunk.length; i += 0x2000) {
      binary += String.fromCharCode(...chunk.subarray(i, i + 0x2000))
    }
    parts.push(btoa(binary))
    await yieldToMain()
  }
  return parts.join('')
}

/**
 * util.ts encryptData와 같은 형식(`base64(iv):base64(암호문)`)을 조각 단위로 만든다.
 * @param data 평문
 * @param encryptKey 암호화 키(UTF-8 바이트를 그대로 키로 씀)
 */
export async function encryptDataChunked(data: string, encryptKey: string): Promise<string> {
  if (!encryptKey) {
    throw new Error('암호화 키가 설정되지 않았습니다.')
  }
  const iv = CryptoJS.lib.WordArray.random(16)
  const encryptor = CryptoJS.algo.AES.createEncryptor(CryptoJS.enc.Utf8.parse(encryptKey), {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })

  const plain = new TextEncoder().encode(data)
  // 호출자의 JSON.stringify·인코딩과 첫 조각이 한 태스크로 몰리지 않게 먼저 양보한다
  await yieldToMain()
  const cipherParts: Uint8Array[] = []
  for (let offset = 0; offset < plain.length; offset += CRYPT_CHUNK_BYTES) {
    cipherParts.push(
      wordArrayToBytes(
        encryptor.process(bytesToWordArray(plain.subarray(offset, offset + CRYPT_CHUNK_BYTES))),
      ),
    )
    await yieldToMain()
  }
  cipherParts.push(wordArrayToBytes(encryptor.finalize()))

  const cipher = new Uint8Array(cipherParts.reduce((sum, part) => sum + part.length, 0))
  let position = 0
  for (const part of cipherParts) {
    cipher.set(part, position)
    position += part.length
  }

  return `${CryptoJS.enc.Base64.stringify(iv)}:${await bytesToBase64(cipher)}`
}
